import React, { useCallback, useRef, useState } from 'react';
import { Alert, Button, Card, notification, Popconfirm, Space, Tag, Upload } from 'antd';
import { ClearOutlined, FileExcelOutlined, SaveOutlined, UploadOutlined } from '@ant-design/icons';
import { AgGridReact } from 'ag-grid-react';
import { ColDef, GridApi } from 'ag-grid-community';
import { useIntl } from 'react-intl';
import client from '../../../config/GraphQLApolloClient';
import { MUTATION_UPLOAD_PARTNER_CONTACTS } from '../obtMutations';
import { QUERY_EXISTING_PARTNER_CONTACTS, QUERY_UPDATE_CONTACT } from '../obtQueries';
import { validatePartnerContactsFile } from '../utils/obtPartnerListValidator';

// ─── Types ────────────────────────────────────────────────────────────────────

interface ParsedContact {
    idpartner?: number; partnerno: string; companycode: string; role: string;
    source?: string; name1?: string; name2?: string; department?: string;
    phone?: string; fax?: string; mail?: string;
}

interface ValidatedContact extends ParsedContact { _status: 'new' | 'duplicate'; _errors: string[]; }
interface ExistingContact { partnerno: string; companycode: string; role: string; email: string; }

// ─── Constants & Helpers ──────────────────────────────────────────────────────

const VALID_ROLES = ['CONTACT','DUNNING','INVOICE','PAD','REJECTION','Prenotif','Interest','OpenItem','Statement','TransConf','BalConf'];
const NUMBERS_ONLY = /^[0-9]+$/;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const validateDept = (d?: string): string | null => {
    if (!d || d.length < 1) return 'Dept empty';
    return d.length > 10 ? 'Dept > 10' : null;
};

const validatePhone = (p?: string): string | null => {
    if (!p) return 'Phone empty';
    return NUMBERS_ONLY.test(p) ? null : 'Phone: digits only';
};

const validateEmail = (m?: string): string | null => {
    if (!m) return 'Email empty';
    return EMAIL_REGEX.test(m) ? null : 'Invalid email';
};

const validateContact = (c: ParsedContact): string[] => {
    if (!c.partnerno) return [];
    const e: string[] = [];
    if (!VALID_ROLES.includes(c.role)) e.push('Invalid role');
    const dErr = validateDept(c.department); if (dErr) e.push(dErr);
    const pErr = validatePhone(c.phone); if (pErr) e.push(pErr);
    const mErr = validateEmail(c.mail); if (mErr) e.push(mErr);
    return e;
};

const isDuplicate = (c: ParsedContact, existing: ExistingContact[]): boolean =>
    existing.some((ec) => ec.partnerno === c.partnerno && ec.companycode === c.companycode && ec.role === c.role && ec.email === c.mail);

// ─── Columns ──────────────────────────────────────────────────────────────────

const STATUS_RENDERER = (p: any) => p.value === 'duplicate' ? <Tag color="orange">Dup</Tag> : <Tag color="green">New</Tag>;
const ERROR_RENDERER = (p: any) => { const errs: string[] = p.value ?? []; return errs.length ? <span style={{ color: '#cf1322' }}>{errs.join('; ')}</span> : null; };

const COLUMN_DEFS: ColDef[] = [
    { field: '_status', headerName: 'Status', width: 90, cellRenderer: STATUS_RENDERER },
    { field: 'partnerno', headerName: 'Partner No', width: 120 },
    { field: 'companycode', headerName: 'Company Code', width: 120 },
    { field: 'role', headerName: 'Role', width: 100 },
    { field: 'mail', headerName: 'Email', flex: 1, minWidth: 170 },
    { field: 'name1', headerName: 'Name 1', width: 120 },
    { field: 'department', headerName: 'Dept', width: 90 },
    { field: 'phone', headerName: 'Phone', width: 110 },
    { field: '_errors', headerName: 'Errors', flex: 1, minWidth: 160, cellRenderer: ERROR_RENDERER },
];
const DEFAULT_COL_DEF: ColDef = { resizable: true, sortable: true };

// ─── Component ────────────────────────────────────────────────────────────────

const ObtUploadPartnerContacts: React.FC = () => {
    const intl = useIntl();
    const [fileList, setFileList] = useState<any[]>([]);
    const [contacts, setContacts] = useState<ValidatedContact[]>([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [dupeCount, setDupeCount] = useState(0);
    const [validationErrors, setValidationErrors] = useState<string[]>([]);
    const gridApiRef = useRef<GridApi | null>(null);

    const handleBeforeUpload = useCallback((file: any) => { setFileList([file]); setValidationErrors([]); return false; }, []);
    const handleRemove = useCallback(() => { setFileList([]); setValidationErrors([]); }, []);
    const handleClear = useCallback(() => { setFileList([]); setContacts([]); setDupeCount(0); setValidationErrors([]); }, []);

    const handleUpload = useCallback(async () => {
        if (fileList.length === 0) {
            notification.warning({ message: intl.formatMessage({ id: 'obt.update.upload.noFile', defaultMessage: 'Select a file first.' }) });
            return;
        }
        const file = fileList[0];
        const validation = await validatePartnerContactsFile(file);
        if (!validation.valid) {
            setValidationErrors(validation.errors);
            return;
        }
        setValidationErrors([]);
        setLoading(true); setContacts([]); setDupeCount(0);
        try {
            const base64 = await new Promise<string>((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = () => resolve((reader.result as string).split(',')[1]);
                reader.onerror = () => reject(reader.error);
                reader.readAsDataURL(file);
            });
            const res = await client.mutate({ mutation: MUTATION_UPLOAD_PARTNER_CONTACTS, variables: { fileBase64: base64, fileName: file.name } });
            const parsed: ParsedContact[] = res?.data?.obtUploadPartnerContactsFile ?? [];
            if (parsed.length === 0) { notification.warning({ message: 'No contacts in file.' }); setLoading(false); return; }

            const partnerNos = Array.from(new Set(parsed.filter((c) => c.partnerno).map((c) => c.partnerno)));
            let existing: ExistingContact[] = [];
            if (partnerNos.length > 0) {
                try {
                    const r = await client.query({ query: QUERY_EXISTING_PARTNER_CONTACTS, variables: { contactsRequest: { partnerno: partnerNos } }, fetchPolicy: 'network-only' });
                    existing = r?.data?.obtExistingPartnerContacts ?? [];
                } catch (err) { console.warn('Dupe check failed:', err); }
            }

            let dupes = 0;
            const validated: ValidatedContact[] = parsed.filter((c) => c.partnerno).map((contact) => {
                const errors = validateContact(contact);
                const dup = isDuplicate(contact, existing);
                if (dup) dupes++;
                return { ...contact, _status: dup ? 'duplicate' as const : 'new' as const, _errors: dup ? ['Duplicate', ...errors] : errors };
            });
            setContacts(validated); setDupeCount(dupes); setFileList([]);
            notification.success({ message: intl.formatMessage({ id: 'obt.update.upload.success', defaultMessage: 'Uploaded.' }), description: `${validated.length} rows` });
        } catch (error) {
            console.error('Upload failed:', error);
            notification.error({ message: intl.formatMessage({ id: 'obt.update.upload.error', defaultMessage: 'Upload failed.' }) });
        } finally { setLoading(false); }
    }, [fileList, intl]);

    const handleSave = useCallback(async () => {
        const valid = contacts.filter((c) => c._status === 'new' && c._errors.length === 0);
        if (valid.length === 0) { notification.warning({ message: 'No valid contacts.' }); return; }
        setSaving(true);
        try {
            for (let i = 0; i < valid.length; i += 5) {
                const batch = valid.slice(i, i + 5);
                await Promise.all(batch.map((c) => {
                    const { _status, _errors, ...data } = c;
                    return client.mutate({ mutation: QUERY_UPDATE_CONTACT, variables: { contactModel: data }, fetchPolicy: 'no-cache' });
                }));
            }
            notification.success({ message: intl.formatMessage({ id: 'obt.update.save.success', defaultMessage: 'Saved.' }), description: `${valid.length} created.` });
            handleClear();
        } catch (error) {
            console.error('Save failed:', error);
            notification.error({ message: intl.formatMessage({ id: 'obt.update.save.error', defaultMessage: 'Save failed.' }) });
        } finally { setSaving(false); }
    }, [contacts, intl, handleClear]);

    const onGridReady = useCallback((params: any) => { gridApiRef.current = params.api; }, []);
    const newValidCount = contacts.filter((c) => c._status === 'new' && c._errors.length === 0).length;

    return (
        <Card title={intl.formatMessage({ id: 'obt.update.uploadPartnerContacts.title', defaultMessage: 'Upload Partner Contacts' })} size="small">
            <p style={{ color: 'rgba(0,0,0,0.65)', marginBottom: 16 }}>
                {intl.formatMessage({ id: 'obt.update.uploadPartnerContacts.description', defaultMessage: 'Upload an Excel file to bulk-create partner contacts.' })}
            </p>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
                <Upload accept=".xlsx,.xls,.xlsm" fileList={fileList} beforeUpload={handleBeforeUpload} onRemove={handleRemove} maxCount={1} showUploadList={false}>
                    <Button icon={<FileExcelOutlined />}>{intl.formatMessage({ id: 'obt.update.upload.selectFile', defaultMessage: 'Select Excel File' })}</Button>
                </Upload>
                {fileList.length > 0 && (
                    <span style={{ fontSize: 13, color: 'rgba(0,0,0,0.65)' }}>{fileList[0]?.name}</span>
                )}
                <Button type="primary" icon={<UploadOutlined />} onClick={handleUpload} loading={loading} disabled={fileList.length === 0}>
                    {intl.formatMessage({ id: 'obt.update.upload.uploadButton', defaultMessage: 'Upload' })}
                </Button>
                <Button icon={<ClearOutlined />} onClick={handleClear} disabled={contacts.length === 0 && fileList.length === 0}>
                    {intl.formatMessage({ id: 'obt.update.upload.clear', defaultMessage: 'Clear' })}
                </Button>
            </div>
            {validationErrors.length > 0 && (
                <Alert
                    type="error"
                    showIcon={true}
                    style={{ marginBottom: 12 }}
                    message={intl.formatMessage({ id: 'obt.upload.validation.title', defaultMessage: 'Invalid file format' })}
                    description={<ul style={{ margin: 0, paddingLeft: 16 }}>{validationErrors.map((e, i) => <li key={i}>{e}</li>)}</ul>}
                />
            )}
            {contacts.length > 0 && (
                <>
                    <Space direction="vertical" style={{ width: '100%', marginBottom: 12 }}>
                        <Alert type="info" showIcon={true} message={`${contacts.length} rows · ${newValidCount} new valid · ${dupeCount} duplicates`} />
                        {dupeCount > 0 && (
                            <Alert type="warning" showIcon={true} message={intl.formatMessage(
                                { id: 'obt.update.uploadPartnerContacts.duplicatesFound', defaultMessage: '{count} duplicates excluded.' },
                                { count: dupeCount }
                            )} />
                        )}
                    </Space>
                    <div className="ag-theme-balham" style={{ height: 320, width: '100%', marginBottom: 12 }}>
                        <AgGridReact
                            rowData={contacts}
                            columnDefs={COLUMN_DEFS}
                            defaultColDef={DEFAULT_COL_DEF}
                            animateRows={true}
                            onGridReady={onGridReady}
                            getRowStyle={(params) => {
                                if (params.data?._status === 'duplicate') return { opacity: '0.6', backgroundColor: '#fffbe6' };
                                if (params.data?._errors?.length > 0) return { backgroundColor: '#fff1f0' };
                                return undefined;
                            }}
                        />
                    </div>
                    <Popconfirm
                        title={intl.formatMessage({ id: 'obt.update.uploadPartnerContacts.save.confirm', defaultMessage: 'Save contacts?' })}
                        onConfirm={handleSave} okText="Yes" cancelText="No" disabled={newValidCount === 0}
                    >
                        <Button type="primary" icon={<SaveOutlined />} loading={saving} disabled={newValidCount === 0}>
                            {intl.formatMessage({ id: 'obt.update.uploadPartnerContacts.save', defaultMessage: 'Save Contacts' })}
                            {newValidCount > 0 ? ` (${newValidCount})` : ''}
                        </Button>
                    </Popconfirm>
                </>
            )}
        </Card>
    );
};

export default ObtUploadPartnerContacts;

